import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';

type Theme = 'dark' | 'light';

interface ThemeContextValue {
  theme: Theme;
  toggle: () => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'dark';
    return (localStorage.getItem('fincal-theme') as Theme) || 'dark';
  });

  useLayoutEffect(() => {
    const root = document.documentElement;
    if (theme === 'light') {
      root.classList.add('light');
      root.classList.remove('dark');
    } else {
      root.classList.add('dark');
      root.classList.remove('light');
    }
    localStorage.setItem('fincal-theme', theme);
  }, [theme]);

  const toggle = useCallback(() => {
    const root = document.documentElement;
    
    // 1. Add class to block all transitions immediately
    root.classList.add('no-transitions');

    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    
    // 2. Use requestAnimationFrame to ensure the class is applied BEFORE the state change
    requestAnimationFrame(() => {
      setTheme(nextTheme);
      localStorage.setItem('fincal-theme', nextTheme);

      // 3. Remove no-transitions after the browser has had time to paint the new theme
      // 100ms is a safe window with the optimized CSS selectors
      setTimeout(() => {
        root.classList.remove('no-transitions');
      }, 100); 
    });
  }, [theme]);

  const value = useMemo(() => ({
    theme,
    toggle,
    isDark: theme === 'dark'
  }), [theme, toggle]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeContext must be used inside ThemeProvider');
  return ctx;
};
